import { useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DayPicker } from "react-day-picker";
import {
  format, isSameMonth, addMonths, subMonths, setYear,
} from "date-fns";
import { ChevronLeft, ChevronRight, Pencil, ChevronDown } from "lucide-react";
import "react-day-picker/dist/style.css";

/**
 * DatePickerSheet — Material-3-inspired bottom-sheet date picker in AfriTask's
 * warm palette (terracotta selection, cream header). Replaces the browser's
 * native <input type="date"> across the admin panel.
 *
 * TODO: wire the pencil icon in the header to a manual keyboard-typing input
 * for direct date entry — currently visual polish only.
 */

const TERRACOTTA = "#B45309"; // referenced via inline classes; kept for docs

export default function DatePickerSheet({
  open,
  onOpenChange,
  value,
  onChange,
  minDate,
  maxDate,
  title = "Select date",
}) {
  const initial = value instanceof Date ? value : new Date();
  const [draft, setDraft] = useState(initial);
  const [monthView, setMonthView] = useState(initial);
  const [yearGrid, setYearGrid] = useState(false);

  // Reset draft + month whenever the sheet reopens with a new value.
  useEffect(() => {
    if (open) {
      const d = value instanceof Date ? value : new Date();
      setDraft(d);
      setMonthView(d);
      setYearGrid(false);
    }
  }, [open, value]);

  const commit = () => {
    onChange?.(draft);
    onOpenChange?.(false);
  };
  const cancel = () => onOpenChange?.(false);

  const canPrev = !minDate || !isSameMonth(monthView, minDate);
  const canNext = !maxDate || !isSameMonth(monthView, maxDate);

  const years = useMemo(() => {
    const y = draft.getFullYear();
    const range = [];
    for (let i = y - 20; i <= y + 20; i++) range.push(i);
    return range;
  }, [draft]);

  const pickYear = (yr) => {
    const next = setYear(draft, yr);
    setDraft(next);
    setMonthView(setYear(monthView, yr));
    setYearGrid(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Backdrop */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-150"
        />
        {/* Sheet */}
        <DialogPrimitive.Content
          data-testid="date-picker-sheet"
          className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-lg rounded-t-2xl bg-white shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=open]:duration-200 data-[state=closed]:duration-150 sm:mb-4 sm:rounded-2xl"
        >
          {/* --- Cream header --- */}
          <div className="rounded-t-2xl bg-[#FDF6EC] p-4">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <DialogPrimitive.Title
                  className="text-[10px] font-medium uppercase tracking-widest text-neutral-500"
                >
                  {title}
                </DialogPrimitive.Title>
                <p
                  className="mt-1 text-2xl font-semibold text-neutral-900"
                  data-testid="date-picker-selected-label"
                >
                  {format(draft, "EEE, MMM d yyyy")}
                </p>
              </div>
              {/* TODO: wire pencil → manual keyboard entry */}
              <button
                type="button"
                aria-label="Edit date manually (coming soon)"
                className="rounded-full p-2 text-neutral-500 hover:bg-neutral-100"
                onClick={() => { /* TODO */ }}
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* --- Month/year switcher --- */}
          <div className="flex items-center justify-between px-4 py-2">
            <button
              type="button"
              onClick={() => setMonthView(subMonths(monthView, 1))}
              disabled={!canPrev}
              className="rounded-full p-2 text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-30"
              data-testid="date-picker-prev-month"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setYearGrid((v) => !v)}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium text-neutral-800 hover:bg-neutral-100"
              data-testid="date-picker-month-year-toggle"
            >
              {format(monthView, "MMMM yyyy")}
              <ChevronDown className={`h-4 w-4 transition-transform ${yearGrid ? "rotate-180" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setMonthView(addMonths(monthView, 1))}
              disabled={!canNext}
              className="rounded-full p-2 text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-30"
              data-testid="date-picker-next-month"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* --- Body: day grid OR year grid --- */}
          {yearGrid ? (
            <div className="max-h-64 overflow-y-auto px-4 pb-4" data-testid="date-picker-year-grid">
              <div className="grid grid-cols-3 gap-2">
                {years.map((yr) => {
                  const selected = yr === draft.getFullYear();
                  return (
                    <button
                      key={yr}
                      type="button"
                      onClick={() => pickYear(yr)}
                      data-testid={`date-picker-year-${yr}`}
                      className={`rounded-full py-2 text-sm font-medium transition ${
                        selected
                          ? "bg-[#B45309] text-white"
                          : "text-neutral-700 hover:bg-neutral-100"
                      }`}
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="px-4 pb-4">
              <DayPickerStyled
                month={monthView}
                selected={draft}
                onMonthChange={setMonthView}
                onSelect={(d) => {
                  if (!d) return;
                  setDraft(d);
                  if (!isSameMonth(d, monthView)) setMonthView(d);
                }}
                fromDate={minDate}
                toDate={maxDate}
              />
            </div>
          )}

          {/* --- Footer actions --- */}
          <div className="flex items-center justify-end gap-1 border-t border-neutral-100 px-4 py-3">
            <button
              type="button"
              onClick={cancel}
              className="rounded-md px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
              data-testid="date-picker-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={commit}
              className="rounded-md bg-[#B45309] px-5 py-2 text-sm font-semibold text-white hover:bg-[#9A3412]"
              data-testid="date-picker-ok"
            >
              OK
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// -------------------- restyled DayPicker --------------------
function DayPickerStyled({ month, selected, onMonthChange, onSelect, fromDate, toDate }) {
  return (
    <DayPicker
      mode="single"
      month={month}
      selected={selected}
      onSelect={onSelect}
      onMonthChange={onMonthChange}
      fromDate={fromDate}
      toDate={toDate}
      showOutsideDays
      // Hide DayPicker's built-in caption + nav (we render our own above)
      classNames={{
        months: "",
        month: "space-y-1",
        caption: "hidden",
        nav: "hidden",
        table: "w-full border-collapse",
        head_row: "flex justify-between",
        head_cell:
          "w-10 text-center text-[11px] font-medium uppercase tracking-wide text-neutral-500 py-2",
        row: "flex justify-between mt-1",
        cell: "w-10 h-10 p-0 text-center",
        day: "w-10 h-10 rounded-full text-sm font-normal text-neutral-900 hover:bg-neutral-100 transition-colors duration-100 focus:outline-none",
        day_selected:
          "!bg-[#B45309] !text-white !font-semibold hover:!bg-[#9A3412]",
        day_today:
          "border border-[#B45309] text-neutral-900",
        day_outside: "text-neutral-300",
        day_disabled: "text-neutral-300 line-through cursor-not-allowed hover:bg-transparent",
        day_hidden: "invisible",
      }}
      modifiersClassNames={{
        selected: "!bg-[#B45309] !text-white",
      }}
      // Weekday abbreviations: single-letter S M T W T F S
      formatters={{
        formatWeekdayName: (date) => format(date, "eeeee"),
      }}
    />
  );
}
